import { useState, useEffect } from 'react'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { DollarSign, TrendingUp, ShoppingBag, ArrowUp, ArrowDown } from 'lucide-react'

interface RevenueStats {
  totalRevenue: number
  totalSales: number
  averageOrderValue: number
  recentOrders: any[]
  monthlyRevenue: {
    month: string
    revenue: number
    change: number
  }[]
}

export function SellerRevenuePage() {
  const user = useAuthStore((state) => state.user)
  const [stats, setStats] = useState<RevenueStats>({
    totalRevenue: 0,
    totalSales: 0,
    averageOrderValue: 0,
    recentOrders: [],
    monthlyRevenue: []
  })
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState('all') // 'all', 'month', 'week'

  useEffect(() => {
    if (user) {
      fetchRevenueStats()
    }
  }, [user, dateRange])

  const fetchRevenueStats = async () => {
    try {
      setLoading(true)

      // Get orders for this seller
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          buyer:profiles!buyer_id (
            full_name,
            email
          ),
          product:products!product_id (
            title,
            price
          )
        `)
        .eq('seller_id', user?.id)
        .eq('status', 'delivered')
        .order('created_at', { ascending: false })

      if (ordersError) throw ordersError

      // Calculate stats
      const totalRevenue = orders?.reduce((sum, order) => sum + order.final_price, 0) || 0
      const totalSales = orders?.length || 0
      const averageOrderValue = totalSales > 0 ? totalRevenue / totalSales : 0

      // Calculate monthly revenue
      const monthlyRevenue = orders?.reduce((acc: any[], order) => {
        const month = new Date(order.created_at).toLocaleString('default', { month: 'long' })
        const existingMonth = acc.find(m => m.month === month)
        if (existingMonth) {
          existingMonth.revenue += order.final_price
        } else {
          acc.push({
            month,
            revenue: order.final_price,
            change: 0 // Calculate change later
          })
        }
        return acc
      }, []) || []

      // Calculate month-over-month change
      monthlyRevenue.forEach((month, index) => {
        if (index > 0) {
          const previousRevenue = monthlyRevenue[index - 1].revenue
          month.change = ((month.revenue - previousRevenue) / previousRevenue) * 100
        }
      })

      setStats({
        totalRevenue,
        totalSales,
        averageOrderValue,
        recentOrders: orders?.slice(0, 5) || [],
        monthlyRevenue
      })
    } catch (err) {
      console.error('Error fetching revenue stats:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Revenus et Statistiques
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Suivez vos ventes et vos revenus
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={dateRange === 'all' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setDateRange('all')}
            >
              Tout
            </Button>
            <Button
              variant={dateRange === 'month' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setDateRange('month')}
            >
              Ce mois
            </Button>
            <Button
              variant={dateRange === 'week' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setDateRange('week')}
            >
              Cette semaine
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Revenus Totaux
                    </dt>
                    <dd className="text-xl sm:text-2xl font-semibold text-gray-900">
                      ${stats.totalRevenue.toFixed(2)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShoppingBag className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Ventes Totales
                    </dt>
                    <dd className="text-xl sm:text-2xl font-semibold text-gray-900">
                      {stats.totalSales}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Valeur Moyenne
                    </dt>
                    <dd className="text-xl sm:text-2xl font-semibold text-gray-900">
                      ${stats.averageOrderValue.toFixed(2)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Revenue Chart */}
        <div className="mt-6 bg-white shadow rounded-lg border border-gray-200">
          <div className="p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900">
              Revenus Mensuels
            </h3>
            <div className="mt-4 overflow-x-auto">
              <div className="min-w-[400px]">
                {stats.monthlyRevenue.length > 0 ? (
                  stats.monthlyRevenue.map((month) => (
                    <div key={month.month} className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {month.month}
                        </span>
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900 mr-2">
                            ${month.revenue.toFixed(2)}
                          </span>
                          {month.change !== 0 && (
                            <span className={`flex items-center text-xs sm:text-sm font-medium ${
                              month.change > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {month.change > 0 ? (
                                <ArrowUp className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              ) : (
                                <ArrowDown className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              )}
                              {Math.abs(month.change).toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="relative w-full h-2 bg-gray-100 rounded">
                        <div
                          className="absolute left-0 top-0 h-full bg-primary rounded"
                          style={{
                            width: `${(month.revenue / Math.max(...stats.monthlyRevenue.map(m => m.revenue))) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Aucune donnée de revenu disponible</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="mt-6 bg-white shadow rounded-lg border border-gray-200">
          <div className="p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900">
              Commandes Récentes
            </h3>
            <div className="mt-4 overflow-x-auto">
              <div className="min-w-[600px]">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="py-3 pl-4 pr-3 text-left text-xs font-semibold text-gray-900">
                        Produit
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900">
                        Client
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900">
                        Date
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900">
                        Montant
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {stats.recentOrders.length > 0 ? (
                      stats.recentOrders.map((order) => (
                        <tr key={order.id}>
                          <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm">
                            <div className="font-medium text-gray-900 line-clamp-1">
                              {order.product.title}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            {order.buyer.full_name || 'Client'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                            ${order.final_price.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-4 text-center text-sm text-gray-500">
                          Aucune commande récente
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}